/**
 * The book checksum (docs/04 §9.3, `asyncapi.BookSnapshotData.checksum`).
 *
 * `seq` catches **dropped** frames; the checksum catches **misapplied** ones,
 * which is the bug class that actually ships — an off-by-one in a delta
 * application leaves the sequence perfectly contiguous and the ladder wrong. On
 * a 1¢ tick, on a market that settles in under a minute, a phantom level is a
 * price the user can click.
 *
 * The construction, verbatim from the spec:
 *
 * > CRC32 over the top `min(25, depth)` levels per side, `price:size` joined by
 * > `:`, computed on the **6dp strings exactly as sent**, bids (descending) then
 * > asks (ascending).
 *
 * "Exactly as sent" is the load-bearing phrase and the reason `size` is passed
 * through untouched rather than re-formatted: the whole point is to hash the
 * bytes the server hashed. A client that normalises `"1250.000000"` to `"1250"`
 * computes a different number and re-snapshots forever.
 *
 * `checksum_levels` (25) is a `config.ws` value, not a constant here — plan §2.12.
 * It is a parameter of every function below and never a default.
 *
 * **The CRC32 itself is pinned by the standard vector** (`"123456789"` →
 * `0xCBF43926`) rather than by a number lifted from the spec's examples. Those
 * examples do not verify — see `checksum.test.ts` and register §F15c — so a test
 * asserting agreement with one would be asserting agreement with a typo.
 */

/** Reflected CRC-32 (IEEE 802.3), the one every venue means by "CRC32". */
const POLYNOMIAL = 0xedb88320

let table: Uint32Array | null = null

function crcTable(): Uint32Array {
  if (table) return table
  const built = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) c = c & 1 ? (c >>> 1) ^ POLYNOMIAL : c >>> 1
    built[i] = c >>> 0
  }
  table = built
  return built
}

/**
 * CRC-32 of a string's UTF-8 bytes, as an unsigned 32-bit integer.
 *
 * Encoded through `TextEncoder` rather than by char code: every byte the server
 * hashed has to be a byte we hash, and the ladder is ASCII only until the day
 * somebody puts a currency symbol in a size.
 */
export function crc32(input: string): number {
  const t = crcTable()
  const bytes = new TextEncoder().encode(input)
  let crc = 0xffffffff
  for (let i = 0; i < bytes.length; i++) {
    // `noUncheckedIndexedAccess`: the index is bounded by the loop, so the
    // non-null assertions below are the type system asking for a receipt.
    const byte = bytes[i] as number
    crc = (crc >>> 8) ^ (t[(crc ^ byte) & 0xff] as number)
  }
  return (crc ^ 0xffffffff) >>> 0
}

/** One ladder level, in the shape both specs agree on (`BookLevel` /
 *  `OrderBookLevel`): integer cents, decimal-string size. */
export interface ChecksumLevel {
  price_cents: number
  /** The 6dp string exactly as it arrived. Never re-formatted. */
  size: string
}

/**
 * The string the checksum is computed over. Exported because it is the thing
 * worth looking at when a mismatch is being diagnosed — comparing two CRC
 * integers tells you nothing, comparing two ladders tells you everything.
 */
export function checksumPayload(
  bids: readonly ChecksumLevel[],
  asks: readonly ChecksumLevel[],
  levels: number,
): string {
  const take = (side: readonly ChecksumLevel[]): string[] =>
    side.slice(0, levels).map((l) => `${l.price_cents}:${l.size}`)
  return [...take(bids), ...take(asks)].join(':')
}

/**
 * The book checksum.
 *
 * `bids` must already be price-descending and `asks` price-ascending — the order
 * the spec guarantees on the wire and the order the store keeps them in. Sorting
 * here would hide a store that had them backwards, which is precisely the
 * misapplication this function exists to catch.
 */
export function bookChecksum(
  bids: readonly ChecksumLevel[],
  asks: readonly ChecksumLevel[],
  levels: number,
): number {
  return crc32(checksumPayload(bids, asks, levels))
}

/** `min(checksum_levels, depth)` — the frame echoes `depth` so the client can
 *  cover exactly the levels the server covered. */
export function checksumLevelCount(configuredLevels: number, depth: number | undefined): number {
  if (depth === undefined || !Number.isFinite(depth)) return configuredLevels
  return Math.min(configuredLevels, depth)
}
